import { api } from './api';

// Push helpers shared by SettingsPage (enable/resubscribe UI) and the
// subscription self-healing hook mounted in InstallPrompt.

export const urlBase64ToArrayBuffer = (base64String: string): ArrayBuffer => {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  const bytes = Uint8Array.from([...rawData].map((char) => char.charCodeAt(0)));
  return bytes.buffer.slice(bytes.byteOffset, bytes.byteOffset + bytes.byteLength);
};

// --- Subscription <-> VAPID key binding ---------------------------------------
// A push subscription is permanently bound to the applicationServerKey it was
// created with (RFC 8292 token binding). FCM enforces this strictly: if the
// server signs with a different VAPID key than the one the subscription was
// created with, every send fails with 403 "VapidPkHashMismatch" — the classic
// "iOS works, Android doesn't" failure after a key change, because Apple
// devices that subscribed later got the new key. Such a subscription can never
// be fixed by re-registering it; the device must unsubscribe and subscribe
// fresh with the current key.

const bytesEqual = (a: Uint8Array, b: Uint8Array): boolean =>
  a.length === b.length && a.every((value, index) => value === b[index]);

// true = bound to serverKey, false = bound to a DIFFERENT key (undeliverable),
// null = browser does not expose the bound key (cannot verify).
export const subscriptionMatchesServerKey = (
  subscription: PushSubscription,
  serverKeyBase64Url: string,
): boolean | null => {
  const bound = subscription.options?.applicationServerKey;
  if (!bound) {
    return null;
  }
  return bytesEqual(
    new Uint8Array(bound),
    new Uint8Array(urlBase64ToArrayBuffer(serverKeyBase64Url)),
  );
};

// Shared by the SettingsPage enable flow and the self-healing resubscribe:
// reuse the existing browser subscription only when it is (verifiably) bound
// to the current server key; otherwise drop it (browser + server side) and
// create a fresh one. Chrome would reject subscribe() with InvalidStateError
// while a differently-keyed subscription exists, so unsubscribing first is
// mandatory.
export const getOrCreateServerKeySubscription = async (
  registration: ServiceWorkerRegistration,
  serverKeyBase64Url: string,
): Promise<PushSubscription> => {
  const existing = await registration.pushManager.getSubscription();
  if (existing) {
    if (subscriptionMatchesServerKey(existing, serverKeyBase64Url) !== false) {
      return existing;
    }
    // Stale key: release the dead endpoint server-side too (best-effort —
    // the row would otherwise linger until the push service 404/410s it).
    await api.pushUnsubscribe(existing.endpoint).catch(() => undefined);
    await existing.unsubscribe();
  }
  return registration.pushManager.subscribe({
    userVisibleOnly: true,
    applicationServerKey: urlBase64ToArrayBuffer(serverKeyBase64Url),
  });
};

// --- Platform detection ------------------------------------------------------

// iPadOS 13+ masquerades as macOS in the user agent; the touch-points check
// catches it.
export const isIosDevice = (): boolean =>
  /iPad|iPhone|iPod/.test(navigator.userAgent) ||
  (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);

export const isStandalonePwa = (): boolean =>
  window.matchMedia('(display-mode: standalone)').matches ||
  (navigator as Navigator & { standalone?: boolean }).standalone === true;

export type PushSupport = 'ready' | 'ios-install-required' | 'unsupported';

// Web Push availability in this browser/context. On iOS, push exists only
// inside an installed (home screen) app on iOS 16.4+ — a Safari tab never gets
// PushManager, so the user has to install ONVI WORKS first.
export const getPushSupport = (): PushSupport => {
  if (isIosDevice() && !isStandalonePwa()) {
    return 'ios-install-required';
  }
  if (
    !('serviceWorker' in navigator) ||
    !('PushManager' in window) ||
    typeof Notification === 'undefined'
  ) {
    return 'unsupported';
  }
  return 'ready';
};

// --- Subscription self-healing ------------------------------------------------
// sw.js sets this IndexedDB flag (and posts PUSH_RESUBSCRIBE_NEEDED) when its
// `pushsubscriptionchange` handler could not register the rotated subscription
// on the server itself. On the next app open we redo the subscribe through the
// normal CSRF-protected API call. Constants must match apps/web/public/sw.js.

const FLAG_DB = 'whoworks-push';
const FLAG_STORE = 'flags';
const RESUBSCRIBE_FLAG = 'resubscribe-needed';
// Which user this device's push subscription belongs to. App-side only —
// sw.js never reads it; it just flags that a resubscribe is needed.
const OWNER_KEY = 'owner-user-id';
const optOutKey = (userId: number): string => `opted-out-user-${userId}`;

const openFlagDb = (): Promise<IDBDatabase> =>
  new Promise((resolve, reject) => {
    const request = indexedDB.open(FLAG_DB, 1);
    request.onupgradeneeded = () => {
      if (!request.result.objectStoreNames.contains(FLAG_STORE)) {
        request.result.createObjectStore(FLAG_STORE);
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });

const readResubscribeFlag = async (): Promise<boolean> => {
  const db = await openFlagDb();
  try {
    return await new Promise<boolean>((resolve, reject) => {
      const tx = db.transaction(FLAG_STORE, 'readonly');
      const get = tx.objectStore(FLAG_STORE).get(RESUBSCRIBE_FLAG);
      get.onsuccess = () => resolve(get.result === true);
      get.onerror = () => reject(get.error);
    });
  } finally {
    db.close();
  }
};

const clearResubscribeFlag = async (): Promise<void> => {
  const db = await openFlagDb();
  try {
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction(FLAG_STORE, 'readwrite');
      tx.objectStore(FLAG_STORE).delete(RESUBSCRIBE_FLAG);
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });
  } finally {
    db.close();
  }
};

// --- Subscription ownership ----------------------------------------------------
// A push subscription is per-device, but the server binds it to a user. On a
// shared device, self-healing must never re-register the subscription under
// whoever happens to be logged in — so we remember which user enabled push
// here and only heal (or release on logout) for that user.

const readPushOwner = async (): Promise<number | null> => {
  const db = await openFlagDb();
  try {
    return await new Promise<number | null>((resolve, reject) => {
      const tx = db.transaction(FLAG_STORE, 'readonly');
      const get = tx.objectStore(FLAG_STORE).get(OWNER_KEY);
      get.onsuccess = () => resolve(typeof get.result === 'number' ? get.result : null);
      get.onerror = () => reject(get.error);
    });
  } finally {
    db.close();
  }
};

const readPushOptOut = async (userId: number): Promise<boolean> => {
  const db = await openFlagDb();
  try {
    return await new Promise<boolean>((resolve, reject) => {
      const tx = db.transaction(FLAG_STORE, 'readonly');
      const get = tx.objectStore(FLAG_STORE).get(optOutKey(userId));
      get.onsuccess = () => resolve(get.result === true);
      get.onerror = () => reject(get.error);
    });
  } finally {
    db.close();
  }
};

// Called whenever a subscribe succeeds (SettingsPage enable/resubscribe flows
// and the self-healing path below).
export const setPushOwner = async (userId: number): Promise<void> => {
  const db = await openFlagDb();
  try {
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction(FLAG_STORE, 'readwrite');
      tx.objectStore(FLAG_STORE).put(userId, OWNER_KEY);
      // An explicit Enable/Re-subscribe overrides an earlier device opt-out.
      tx.objectStore(FLAG_STORE).delete(optOutKey(userId));
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });
  } finally {
    db.close();
  }
};

const clearPushOwner = async (): Promise<void> => {
  const db = await openFlagDb();
  try {
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction(FLAG_STORE, 'readwrite');
      tx.objectStore(FLAG_STORE).delete(OWNER_KEY);
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });
  } finally {
    db.close();
  }
};

const rememberPushOptOut = async (
  userId: number,
  clearCurrentOwnership = true,
): Promise<void> => {
  const db = await openFlagDb();
  try {
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction(FLAG_STORE, 'readwrite');
      tx.objectStore(FLAG_STORE).put(true, optOutKey(userId));
      if (clearCurrentOwnership) {
        tx.objectStore(FLAG_STORE).delete(OWNER_KEY);
        tx.objectStore(FLAG_STORE).delete(RESUBSCRIBE_FLAG);
      }
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });
  } finally {
    db.close();
  }
};

// Logout hook (useAuth): if this device's subscription belongs to the user who
// is logging out, drop the browser subscription and forget the ownership +
// pending-heal flag, so the next login cannot inherit it. No-op for any other
// stored owner. Callers treat this as best-effort and never block logout on it.
export const releasePushOwnership = async (userId: number): Promise<void> => {
  // Do not race a login/app-open reconciliation. Without this wait a slow
  // subscribe request could finish after logout cleanup and silently recreate
  // the server row for a browser subscription that was already removed.
  await pushReconciliationInFlight?.catch(() => undefined);

  if ((await readPushOwner()) !== userId) {
    return;
  }
  if ('serviceWorker' in navigator) {
    const registration = await navigator.serviceWorker.getRegistration();
    const subscription = await registration?.pushManager.getSubscription();
    if (subscription) {
      // We are still authenticated here (useAuth calls this before /logout), so
      // remove the server row as well as the browser subscription. Previously
      // the dead row stayed behind until a later push happened to return 410.
      await api.pushUnsubscribe(subscription.endpoint).catch(() => undefined);
      await subscription.unsubscribe();
    }
  }
  await clearPushOwner();
  await clearResubscribeFlag();
};

// Explicit Settings action. Unlike logout cleanup, this stores the user's
// intent so app-open reconciliation does not immediately turn push back on.
export const disablePushForUser = async (userId: number): Promise<boolean> => {
  await pushReconciliationInFlight?.catch(() => undefined);

  const owner = await readPushOwner().catch(() => null);
  if (owner !== null && owner !== userId) {
    // Keep the other user's ownership and pending-heal state intact.
    await rememberPushOptOut(userId, false);
    return false;
  }

  let removed = false;
  if ('serviceWorker' in navigator) {
    const registration = await navigator.serviceWorker.getRegistration();
    const subscription = await registration?.pushManager.getSubscription();
    if (subscription) {
      await api.pushUnsubscribe(subscription.endpoint).catch(() => undefined);
      removed = await subscription.unsubscribe();
    }
  }

  await rememberPushOptOut(userId);
  return removed;
};

// Re-register this device's subscription with the server (creating a fresh
// browser subscription when the old one is gone). Mirrors the enable flow in
// SettingsPage but stays silent — no toasts.
const resubscribeNow = async (userId: number): Promise<void> => {
  if (getPushSupport() !== 'ready' || Notification.permission !== 'granted') {
    return;
  }

  const registration = await navigator.serviceWorker.ready;
  const { publicKey } = await api.pushGetPublicKey();
  const subscription = await getOrCreateServerKeySubscription(registration, publicKey);

  const json = subscription.toJSON();
  if (!json.endpoint || !json.keys?.p256dh || !json.keys?.auth) {
    throw new Error('Invalid push subscription payload');
  }

  await api.pushSubscribe({
    endpoint: json.endpoint,
    keys: {
      p256dh: json.keys.p256dh,
      auth: json.keys.auth,
    },
  });
  await setPushOwner(userId);
};

let pushReconciliationInFlight: Promise<void> | null = null;

// Keep the browser subscription and the authenticated user's server row in
// sync on every app open. The old implementation only ran after
// pushsubscriptionchange set an IndexedDB flag. Logging out intentionally
// removed the browser subscription, but logging back in never set that flag,
// leaving Notification.permission === "granted" with no subscription forever.
//
// Re-upserting an existing subscription is intentional: it repairs a missing
// D1 row too (for example after a push service returned 404/410) without asking
// the user to visit Settings and press Re-subscribe.
export const reconcilePushSubscription = async (currentUserId: number): Promise<void> => {
  if (pushReconciliationInFlight) {
    return pushReconciliationInFlight;
  }

  pushReconciliationInFlight = (async () => {
    if (getPushSupport() !== 'ready' || Notification.permission !== 'granted') {
      return;
    }

    // Respect the explicit "Odhlásiť toto zariadenie" action. Logout cleanup
    // does not set this flag, so the same user gets push restored after login.
    if (await readPushOptOut(currentUserId).catch(() => false)) {
      return;
    }

    // Never steal a subscription explicitly owned by a different signed-in
    // user on a shared device. IndexedDB can be unavailable in private mode;
    // in that case continue, because the authenticated API still scopes the
    // subscription to the current account.
    const owner = await readPushOwner().catch(() => null);
    if (owner !== null && owner !== currentUserId) {
      return;
    }

    await resubscribeNow(currentUserId);
    await clearResubscribeFlag().catch(() => undefined);
  })().finally(() => {
    pushReconciliationInFlight = null;
  });

  return pushReconciliationInFlight;
};

export const healPushSubscriptionIfNeeded = async (currentUserId: number): Promise<void> => {
  try {
    if (await readResubscribeFlag()) {
      await reconcilePushSubscription(currentUserId);
    }
  } catch {
    // Flag stays set — retried on the next app open.
  }
};

// Mounted once per app session (InstallPrompt renders inside AppShell on every
// authenticated screen): checks the stored flag and reacts to live
// PUSH_RESUBSCRIBE_NEEDED messages from sw.js. Healing is scoped to the
// signed-in user. Returns a cleanup function.
export const registerPushSelfHealing = (currentUserId: number): (() => void) => {
  // This is deliberately unconditional. reconcilePushSubscription exits
  // without prompting when permission is not granted, and otherwise repairs
  // both a missing browser subscription and a missing server row.
  void reconcilePushSubscription(currentUserId);

  if (!('serviceWorker' in navigator)) {
    return () => undefined;
  }

  const onMessage = (event: MessageEvent) => {
    if ((event.data as { type?: string } | null)?.type === 'PUSH_RESUBSCRIBE_NEEDED') {
      void reconcilePushSubscription(currentUserId);
    }
  };

  navigator.serviceWorker.addEventListener('message', onMessage);
  return () => navigator.serviceWorker.removeEventListener('message', onMessage);
};
